import { useEffect, useRef, useState } from 'react'
import { getCountries, getCountryCallingCode } from 'libphonenumber-js'

const flag = code => [...code.toUpperCase()].map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65)).join('')

export const COUNTRY_LIST = getCountries().map(code => ({
  code,
  dial: '+' + getCountryCallingCode(code),
  label: new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code,
  flag: flag(code),
})).sort((a, b) => a.label.localeCompare(b.label))

export default function CountryCodeSelect({ value, onChange }) {
  const [open, setOpen]   = useState(false)
  const [query, setQuery] = useState('')
  const ref               = useRef(null)
  const searchRef         = useRef(null)

  const selected = COUNTRY_LIST.find(c => c.dial === value) ?? COUNTRY_LIST.find(c => c.code === 'IN')

  const filtered = query.trim()
    ? COUNTRY_LIST.filter(c =>
        c.label.toLowerCase().includes(query.toLowerCase()) ||
        c.dial.includes(query)
      )
    : COUNTRY_LIST

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery('') }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 50)
  }, [open])

  return (
    <div ref={ref} className="relative shrink-0" style={{ width: '105px' }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between gap-1 px-3 py-2.5 rounded-xl text-sm outline-none transition-all"
        style={{
          background: 'var(--surface-2)',
          border: `1px solid ${open ? 'var(--brand)' : 'var(--border)'}`,
          color: 'var(--text-1)',
          boxShadow: open ? '0 0 0 2px color-mix(in srgb, var(--brand) 20%, transparent)' : 'none',
        }}
      >
        <span className="truncate text-sm">{selected?.flag} {selected?.dial}</span>
        <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
             style={{ color: 'var(--text-3)' }}
             viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 4l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 rounded-xl shadow-2xl z-50 flex flex-col"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: '240px', maxHeight: '260px' }}>
          <div className="p-2 border-b" style={{ borderColor: 'var(--border)' }}>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search country…"
              className="w-full px-3 py-1.5 rounded-lg text-xs outline-none"
              style={{ background: 'var(--surface-2)', color: 'var(--text-1)', border: '1px solid var(--border)' }}
              onFocus={e => { e.currentTarget.style.borderColor = 'var(--brand)' }}
              onBlur={e  => { e.currentTarget.style.borderColor = 'var(--border)' }}
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <p className="text-xs px-3 py-3 text-center" style={{ color: 'var(--text-3)' }}>No results</p>
            )}
            {filtered.map(c => {
              const sel = c.dial === value
              return (
                <button
                  key={c.code}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); onChange(c.dial); setOpen(false); setQuery('') }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs transition-colors"
                  style={{
                    background: sel ? 'color-mix(in srgb, var(--brand) 12%, var(--surface))' : 'transparent',
                    color: sel ? 'var(--brand)' : 'var(--text-1)',
                  }}
                  onMouseOver={e => { if (!sel) e.currentTarget.style.background = 'var(--surface-2)' }}
                  onMouseOut={e  => { if (!sel) e.currentTarget.style.background = 'transparent' }}
                >
                  <span className="text-base leading-none">{c.flag}</span>
                  <span className="flex-1 truncate">{c.label}</span>
                  <span className="shrink-0 font-mono" style={{ color: 'var(--text-3)' }}>{c.dial}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
